import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

const NormalAlert = ({
  show,
  type = "info",
  message,
  duration = 3000,
  onClose,
  position = "bottom-right",
}) => {
  const [progress, setProgress] = useState(100);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);

    setProgress(100);

    rafRef.current = requestAnimationFrame(() => {
      setProgress(0);
    });

    timerRef.current = setTimeout(() => {
      onClose?.();
    }, duration);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(timerRef.current);
    };
  }, [show, duration, onClose]);

  if (!show) return null;

  const styles = {
    success: {
      border: "border-green-400",
      icon: <CheckCircle className="text-green-500" size={20} />,
      bar: "bg-green-500",
    },
    error: {
      border: "border-red-400",
      icon: <XCircle className="text-red-500" size={20} />,
      bar: "bg-red-500",
    },
    warning: {
      border: "border-yellow-400",
      icon: <AlertTriangle className="text-yellow-500" size={20} />,
      bar: "bg-yellow-500",
    },
    info: {
      border: "border-blue-400",
      icon: <Info className="text-blue-500" size={20} />,
      bar: "bg-blue-500",
    },
  };

  const style = styles[type] || styles.info;

  return createPortal(
    <>
      {/* Background ABOVE modal */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-[10000]" />

      {/* Alert Box */}
      <div
        className={`fixed z-[10001] animate-slide-in ${
          position === "center"
            ? "inset-0 flex items-center justify-center"
            : "bottom-6 right-6"
        }`}
      >
        <div
          className={`bg-white border ${style.border} rounded-lg shadow-lg min-w-[340px] overflow-hidden`}
        >
          <div className="w-full h-[3px] bg-white">
            <div
              className={`h-full ${style.bar}`}
              style={{
                width: `${progress}%`,
                transition: `width ${duration}ms linear`,
              }}
            />
          </div>

          <div className="flex items-start gap-3 px-4 py-5">
            {style.icon}
            <p className="flex-1 text-sm font-medium text-gray-800">
              {message}
            </p>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px) translateX(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0) translateX(0);
          }
        }
        .animate-slide-in {
          animation: slideIn 0.25s ease-out;
        }
      `}</style>
    </>,
    document.body
  );
};

export default NormalAlert;
